import {
  boolean,
  date,
  number,
  object,
  SchemaOf,
  string,
  AnySchema,
  array,
} from "yup";
import { IPaf, IPafApplication } from "../../_api";
import { pafAEVFields, pafApplicationFields } from "../../_appData";

type PafSchemaOptions = {
  isLicenseeRequired: boolean;
};

const requiredIf = <T extends AnySchema>(
  condition: boolean,
  schema: T
): T => (condition ? schema.required() : schema.optional());

export const createPafSchema = (
  options: PafSchemaOptions
): SchemaOf<IPaf> => {
  const { isLicenseeRequired } = options;


  return object({
    paf_id: string().max(36).optional(),
    paf_sid: string().max(10).optional(),
    customer_id: number().integer().optional(),
    listadmin_id: number().integer().optional().nullable(),
    licensee_id: number().integer().optional(),
    is_foreign_location: boolean().label(pafAEVFields.isForeignLocation.label).required(),
    is_ncoa: boolean().label(pafAEVFields.isNcoa.label).required(),
    is_dsf: boolean().label(pafAEVFields.isDsf.label).required(),
    is_triplex: boolean().label(pafAEVFields.isTriplex.label).required(),
    is_waiver_on_file: boolean().label(pafAEVFields.isWaiverOnFile.label).required(),
    is_internal: boolean().optional(),
    list_name: string().label(pafAEVFields.listName.label).max(50).optional(),
    comments: string().label(pafAEVFields.comments.label).max(500).optional(),
    notes: string().label(pafAEVFields.notes.label).max(500).optional(),
    list_owner_signature_date: date().label(pafAEVFields.customerSignedDatetime.label).optional().nullable(),
    list_admin_signature_date: date().label(pafAEVFields.brokerSignedDatetime.label).optional().nullable(),
    licensee_signature_date: date().label(pafAEVFields.licenseeSignedDatetime.label).optional().nullable(),
    licensee_officer: string().label(pafAEVFields.licenseeOfficerName.label).optional(),
    licensee_acct_exec: requiredIf(
      isLicenseeRequired,
      string().label(pafAEVFields.licenseeAcctExec.label)
    ),

    licensee_sales_office: string().optional(),

    licensee_operator: requiredIf(
      isLicenseeRequired,
      string().label(pafAEVFields.licenseeOperator.label)
    ),
    frequency: number().label(pafAEVFields.frequency.label).optional(),
    status: number().label(pafAEVFields.status.label).optional(),
    signature_status: string().label(pafAEVFields.signatureStatus.label).optional(),
    is_deleted: boolean().label(pafAEVFields.isDeleted.label).optional(),
    expiration_date: date().label(pafAEVFields.expirationDate.label).optional(),
    create_datetime: date().label(pafAEVFields.createDatetime.label).optional(),
    modify_datetime: date().label(pafAEVFields.modifyDatetime.label).optional(),
    modify_user_name: string()
      .label(pafAEVFields.modifyUserName.label)
      .optional()

      .transform((value) => (value == null ? '' : value)),
    company__company_name: string().label(pafAEVFields.companyName.label).max(128).required(),
    company__parent_company_name: string().label(pafAEVFields.parentCompanyName.label).max(128).optional(),
    company__dba_name: string().label(pafAEVFields.dbaName.label).max(128).optional(),
    company__address1: string().label(pafAEVFields.address1.label).max(80).required(),
    company__address2: string().label(pafAEVFields.address2.label).max(80).optional(),
    company__city: string().label(pafAEVFields.city.label).max(80).required(),
    company__state: string().label(pafAEVFields.state.label).max(5).required(),
    company__postal_code: string().label(pafAEVFields.postalCode.label).max(25).required(),
    company__urbanization: string().label(pafAEVFields.urbanization.label).max(28).optional(),
    company__federal_tax_id: string().label(pafAEVFields.federalTaxId.label).max(30).optional(),
    company__naics_code: string().label(pafAEVFields.naicsCode.label).max(10).required(),
    company__usps_mailer_id: string()
      .transform((value) => value == null ? '' : value)
      .label(pafAEVFields.uspsMailerId.label)
      .max(30)
      .optional(),
    company__company_registration_id: string().label(pafAEVFields.customerRegistrationId.label).max(15).optional(),
    company__phone: string().label(pafAEVFields.phone.label).max(25).required(),
    company__representative_name: string().label(pafAEVFields.representativeName.label).max(100).required(),
    company__representative_title: string()
      .label(pafAEVFields.representativeTitle.label)
      .max(100)
      .optional()
      .default('')
      .transform((value) => value == null ? '' : value),
    company__representative_email: string().email().label(pafAEVFields.representativeEmail.label).max(128).required(),
    company__company_website: string().label(pafAEVFields.companyWebsite.label).max(128).optional(),
    company__customer_since_date: date().label(pafAEVFields.customerSinceDate.label).optional(),
    company__is_deleted: boolean().label(pafAEVFields.customerIsDeleted.label).optional(),
    company__is_internal: boolean().label(pafAEVFields.customerIsInternal.label).optional(),
    listadmin__listadmin_sid: string().label(pafAEVFields.listAdminSid.label).max(10).optional().nullable(),
    listadmin__company_name: string().label(pafAEVFields.listAdminCompanyName.label).max(128).optional().nullable(),
    listadmin__address1: string().label(pafAEVFields.listAdminAddress1.label).max(80).optional().nullable(),
    listadmin__address2: string().label(pafAEVFields.listAdminAddress2.label).max(80).optional().nullable(),
    listadmin__city: string().label(pafAEVFields.listAdminCity.label).max(80).optional().nullable(),
    listadmin__state: string().label(pafAEVFields.listAdminState.label).max(5).optional().nullable(),
    listadmin__postal_code: string().label(pafAEVFields.listAdminPostalCode.label).max(25).optional().nullable(),
    listadmin__federal_tax_id: string().label(pafAEVFields.listAdminFederalTaxId.label).max(30).optional().nullable(),
    listadmin__naics_code: string().label(pafAEVFields.listAdminNaicsCode.label).max(10).optional().nullable(),
    listadmin__phone: string().label(pafAEVFields.listAdminPhone.label).max(25).optional().nullable(),
    listadmin__representative_name: string().label(pafAEVFields.listAdminRepresentativeName.label).max(100).optional().nullable(),
    listadmin__representative_title: string().label(pafAEVFields.listAdminRepresentativeTitle.label).max(100).optional().nullable(),
    listadmin__representative_email: string().label(pafAEVFields.listAdminRepresentativeEmail.label).max(128).optional().nullable(),
    licensee__company_name: string().label(pafAEVFields.licenseeCompanyName.label).max(128).optional(),
    licensee__federal_tax_id: string().label(pafAEVFields.licenseeFederalTaxId.label).max(30).optional(),
    licensee__phone: string().label(pafAEVFields.licenseePhone.label).max(25).optional(),
    licensee__fax: string().label(pafAEVFields.licenseeFax.label).max(25).optional(),
    licensee__officer_name: string().label(pafAEVFields.licenseeOfficerName.label).max(100).optional(),
    licensee__officer_title: string().label(pafAEVFields.licenseeOfficerTitle.label).max(100).optional(),
    listadmin_signature_text: string()
      .transform((value) => value == null ? '' : value)
      .label(pafAEVFields.listAdminSignatureText.label)
      .optional(),
    listadmin_signature_date: date().label(pafAEVFields.listAdminSignatureDate.label).optional().nullable(),
    list_owner_signature_text: string()
      .transform((value) => value == null ? '' : value)
      .label(pafAEVFields.listOwnerSignatureText.label)
      .optional(),
    licensee_signature_text: string().label(pafAEVFields.licenseeSignatureText.label).optional(),
    pending_signatures: array().of(string()).optional(),
  })
};

export const pafSchema: SchemaOf<IPafApplication> = object({
  paf_id: string().max(36).optional(),
  paf_sid: string().max(10).optional(),
  is_foreign_location: boolean().label(pafApplicationFields.isForeignLocation.label).required(),
  is_ncoa: boolean().label(pafApplicationFields.isNcoa.label).required(),
  is_dsf: boolean().label(pafApplicationFields.isDsf.label).required(),
  is_triplex: boolean().label(pafApplicationFields.isTriplex.label).required(),
  is_waiver_on_file: boolean().label(pafApplicationFields.isWaiverOnFile.label).required(),
  list_name: string().label(pafApplicationFields.listName.label).max(50).optional(),
  comments: string().label(pafApplicationFields.comments.label).max(500).optional(),
  notes: string().label(pafApplicationFields.notes.label).max(500).optional(),
  // removed duplicate signature date fields; use the ones at the end of the schema
  licensee_signature_date: date().label(pafApplicationFields.licenseeSignedDatetime.label).optional().nullable(),
  frequency: number().label(pafApplicationFields.frequency.label).optional(),
  status: number().label(pafApplicationFields.status.label).optional(),
  signature_status: string().label(pafApplicationFields.signatureStatus.label).optional(),
  is_deleted: boolean().label(pafApplicationFields.isDeleted.label).required(),
  expiration_date: date().label(pafApplicationFields.expirationDate.label).optional(),
  create_datetime: date().label(pafApplicationFields.createDatetime.label).optional(),
  modify_datetime: date().label(pafApplicationFields.modifyDatetime.label).optional(),
  modify_user_name: string()
    .label(pafApplicationFields.modifyUserName.label)
    .optional()
    .transform((value) => (value == null ? '' : value)),
  customer_id: number().integer().optional(),
  company__company_name: string().label(pafApplicationFields.companyName.label).max(128).required(),
  company__parent_company_name: string().label(pafApplicationFields.parentCompanyName.label).max(128).optional(),
  company__dba_name: string().label(pafApplicationFields.dbaName.label).max(128).optional(),
  company__address1: string().label(pafApplicationFields.address1.label).max(80).required(),
  company__address2: string().label(pafApplicationFields.address2.label).max(80).optional(),
  company__city: string().label(pafApplicationFields.city.label).max(80).required(),
  company__state: string().label(pafApplicationFields.state.label).max(5).required(),
  company__postal_code: string().label(pafApplicationFields.postalCode.label).max(25).required(),
  company__urbanization: string().label(pafApplicationFields.urbanization.label).max(28).optional(),
  company__federal_tax_id: string().label(pafApplicationFields.federalTaxId.label).max(30).optional(),
  company__naics_code: string().label(pafApplicationFields.naicsCode.label).max(10).required(),
  company__usps_mailer_id: string()
    .transform((value) => value == null ? '' : value)
    .label(pafApplicationFields.uspsMailerId.label)
    .max(30)
    .optional(),
  company__company_registration_id: string().label(pafApplicationFields.customerRegistrationId.label).max(15).optional(),
  company__phone: string().label(pafApplicationFields.phone.label).max(25).required(),
  company__representative_name: string().label(pafApplicationFields.representativeName.label).max(100).required(),
  company__representative_title: string().label(pafApplicationFields.representativeTitle.label).max(100).optional(),
  company__representative_email: string().email().label(pafApplicationFields.representativeEmail.label).max(128).required(),
  company__company_website: string().label(pafApplicationFields.companyWebsite.label).max(128).optional(),
  company__customer_since_date: date().label(pafApplicationFields.customerSinceDate.label).optional(),
  company__is_deleted: boolean().label(pafApplicationFields.customerIsDeleted.label).required(),
  company__is_internal: boolean().label(pafApplicationFields.customerIsInternal.label).required(),
  listadmin_id: number().integer().optional().nullable(),
  listadmin__listadmin_sid: string().label(pafApplicationFields.listAdminSid.label).max(10).optional().nullable(),
  listadmin__company_name: string().label(pafApplicationFields.listAdminCompanyName.label).max(128).optional().nullable(),
  listadmin__address1: string().label(pafApplicationFields.listAdminAddress1.label).max(80).optional().nullable(),
  listadmin__address2: string().label(pafApplicationFields.listAdminAddress2.label).max(80).optional().nullable(),
  listadmin__city: string().label(pafApplicationFields.listAdminCity.label).max(80).optional().nullable(),
  listadmin__state: string().label(pafApplicationFields.listAdminState.label).max(5).optional().nullable(),
  listadmin__postal_code: string().label(pafApplicationFields.listAdminPostalCode.label).max(25).optional().nullable(),
  listadmin__federal_tax_id: string().label(pafApplicationFields.listAdminFederalTaxId.label).max(30).optional().nullable(),
  listadmin__naics_code: string().label(pafApplicationFields.listAdminNaicsCode.label).max(10).optional().nullable(),
  listadmin__phone: string().label(pafApplicationFields.listAdminPhone.label).max(25).optional().nullable(),
  listadmin__representative_name: string().label(pafApplicationFields.listAdminRepresentativeName.label).max(100).optional().nullable(),
  listadmin__representative_title: string().label(pafApplicationFields.listAdminRepresentativeTitle.label).max(100).optional().nullable(),
  listadmin__representative_email: string().label(pafApplicationFields.listAdminRepresentativeEmail.label).max(128).optional().nullable(),
  licensee_id: number().integer().optional(),
  licensee_officer: string().label(pafApplicationFields.licenseeOfficerName.label).optional(),
  licensee_acct_exec: string().label(pafApplicationFields.licenseeAcctExec.label).optional(),
  licensee_sales_office: string().label(pafApplicationFields.licenseeSalesOffice.label).optional(),
  licensee_operator: string().label(pafApplicationFields.licenseeOperator.label).optional(),
  licensee__company_name: string().label(pafApplicationFields.licenseeCompanyName.label).max(128).optional(),
  licensee__federal_tax_id: string().label(pafApplicationFields.licenseeFederalTaxId.label).max(30).optional(),
  licensee__phone: string().label(pafApplicationFields.licenseePhone.label).max(25).optional(),
  licensee__fax: string().label(pafApplicationFields.licenseeFax.label).max(25).optional(),
  licensee__officer_name: string().label(pafApplicationFields.licenseeOfficerName.label).max(100).optional(),
  licensee__officer_title: string().label(pafApplicationFields.licenseeOfficerTitle.label).max(100).optional(),
  licensee_signature_attest: boolean().label(pafApplicationFields.licenseeSignatureAttest.label).optional(),
  licensee_signature_text: string().label(pafApplicationFields.licenseeSignatureText.label).optional(),
  licensee_signature_rejection_reason: string().optional(),
  list_admin_signature_attest: boolean().label(pafApplicationFields.listAdminSignatureAttest.label).optional(),
  list_admin_signature_text: string()
    .transform((value) => value == null ? '' : value)
    .label(pafApplicationFields.listAdminSignatureText.label)
    .optional(),
  list_admin_signature_date: date().label(pafApplicationFields.listAdminSignatureDate.label).optional().nullable(),
  list_owner_signature_attest: boolean().label(pafApplicationFields.listOwnerSignatureAttest.label).optional(),
  list_owner_signature_text: string()
    .transform((value) => value == null ? '' : value)
    .label(pafApplicationFields.listOwnerSignatureText.label)
    .optional(),
  list_owner_signature_date: date().label(pafApplicationFields.listOwnerSignatureDate.label).optional().nullable(),
});
